import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, ArrowRight, Shield, Building2, FileText, Clock, BarChart3, CheckCircle, Info, Sparkles } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
export function AppDashboard() {
  const navigate = useNavigate();
  const {
    user,
    preferences,
    business,
    profile
  } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const showBusiness = business?.hasBusiness === true;
  const preferPersonal = preferences?.startWith === 'personal' || preferences?.startWith === 'both' || !preferences?.startWith;
  const preferBusiness = preferences?.startWith === 'business';
  const getRecommendedAction = () => {
    if (preferBusiness && showBusiness) {
      return {
        label: 'Start your Business Legal Health Check',
        path: '/app/legal-health-check',
        state: {
          type: 'business',
          viewState: 'setup'
        }
      };
    }
    return {
      label: 'Start your Personal Legal Health Check',
      path: '/app/legal-health-check',
      state: {
        type: 'personal',
        viewState: 'setup'
      }
    };
  };
  const recommended = getRecommendedAction();
  return <AppLayout data-id="element-279">
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-280">
        {/* Welcome Header */}
        <div className="mb-8" data-id="element-281">
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-3xl font-bold text-primary mb-2" data-id="element-282">
            Welcome back, {firstName}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="text-secondary" data-id="element-283">
            {profile?.province ? `Based in ${profile.province}` : 'Your legal health dashboard'}
            {preferences?.motivations && preferences.motivations.length > 0 && <span data-id="element-284">
                {' '}
                · Interested in {preferences.motivations.slice(0, 2).join(', ')}
                {preferences.motivations.length > 2 ? ` +${preferences.motivations.length - 2} more` : ''}
              </span>}
          </motion.p>
        </div>

        {/* Recommended Next Action */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-8 bg-primary rounded-xl p-6 sm:p-8 relative overflow-hidden" data-id="element-285">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-286"></div>
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-287">
            <div className="flex items-center gap-4" data-id="element-288">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0" data-id="element-289">
                <Sparkles className="w-6 h-6 text-white" data-id="element-290" />
              </div>
              <div data-id="element-291">
                <p className="text-white/70 text-sm font-medium mb-1" data-id="element-292">
                  Recommended next step
                </p>
                <h3 className="text-xl font-bold text-white" data-id="element-293">
                  {recommended.label}
                </h3>
              </div>
            </div>
            <button onClick={() => navigate(recommended.path, {
            state: recommended.state
          })} className="flex items-center px-6 py-3 bg-white text-primary rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-lg flex-shrink-0" data-id="element-294">
              Get Started <ArrowRight className="w-4 h-4 ml-2" data-id="element-295" />
            </button>
          </div>
        </motion.div>

        {/* Primary Action Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-296">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15
        }} className={`bg-white rounded-xl border border-borderColor p-6 sm:p-8 shadow-sm ${preferPersonal && !preferBusiness ? 'ring-2 ring-turquoise/30' : ''}`} data-id="element-297">
            <div className="flex items-start justify-between mb-4" data-id="element-298">
              <div className="w-12 h-12 bg-aliceBlue rounded-xl flex items-center justify-center" data-id="element-299">
                <Shield className="w-6 h-6 text-turquoise" data-id="element-300" />
              </div>
              {preferPersonal && !preferBusiness && <span className="px-2.5 py-1 bg-turquoise/10 text-turquoise text-xs font-semibold rounded-full" data-id="element-301">
                  Recommended
                </span>}
            </div>
            <h2 className="text-xl font-bold text-primary mb-2" data-id="element-302">
              Personal Legal Health Check
            </h2>
            <p className="text-secondary text-sm mb-6" data-id="element-303">
              Covers family, estate planning, property, work, and personal legal
              readiness across 6 key areas.
            </p>
            <div className="flex items-center gap-4 text-xs text-secondary mb-6" data-id="element-304">
              <span className="flex items-center gap-1" data-id="element-305">
                <Clock className="w-3.5 h-3.5" data-id="element-306" /> ~5 min
              </span>
              <span className="flex items-center gap-1" data-id="element-307">
                <BarChart3 className="w-3.5 h-3.5" data-id="element-308" /> 24 questions
              </span>
            </div>
            <button onClick={() => navigate('/app/legal-health-check', {
            state: {
              type: 'personal',
              viewState: 'setup'
            }
          })} className="w-full flex items-center justify-center px-6 py-3 bg-violetBlue text-white rounded-lg font-semibold hover:opacity-90 transition-opacity" data-id="element-309">
              Start Assessment <ArrowRight className="w-4 h-4 ml-2" data-id="element-310" />
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className={`bg-white rounded-xl border border-borderColor p-6 sm:p-8 shadow-sm ${preferBusiness ? 'ring-2 ring-violetBlue/30' : ''} ${!showBusiness ? 'opacity-60' : ''}`} data-id="element-311">
            <div className="flex items-start justify-between mb-4" data-id="element-312">
              <div className="w-12 h-12 bg-aliceBlue rounded-xl flex items-center justify-center" data-id="element-313">
                <Building2 className="w-6 h-6 text-violetBlue" data-id="element-314" />
              </div>
              {preferBusiness && showBusiness && <span className="px-2.5 py-1 bg-violetBlue/10 text-violetBlue text-xs font-semibold rounded-full" data-id="element-315">
                  Recommended
                </span>}
            </div>
            <h2 className="text-xl font-bold text-primary mb-2" data-id="element-316">
              Business Legal Health Check
            </h2>
            <p className="text-secondary text-sm mb-6" data-id="element-317">
              {showBusiness ? `Evaluate ${business?.name || 'your business'} across contracts, HR, compliance, and corporate structure.` : 'Evaluate your business across contracts, HR, compliance, and corporate structure.'}
            </p>
            <div className="flex items-center gap-4 text-xs text-secondary mb-6" data-id="element-318">
              <span className="flex items-center gap-1" data-id="element-319">
                <Clock className="w-3.5 h-3.5" data-id="element-320" /> ~5 min
              </span>
              <span className="flex items-center gap-1" data-id="element-321">
                <BarChart3 className="w-3.5 h-3.5" data-id="element-322" /> 18 questions
              </span>
            </div>
            {showBusiness ? <button onClick={() => navigate('/app/legal-health-check', {
            state: {
              type: 'business',
              viewState: 'setup'
            }
          })} className="w-full flex items-center justify-center px-6 py-3 bg-white border-2 border-violetBlue text-violetBlue rounded-lg font-semibold hover:bg-aliceBlue transition-colors" data-id="element-323">
                Start Assessment <ArrowRight className="w-4 h-4 ml-2" data-id="element-324" />
              </button> : <div className="w-full text-center py-3 text-sm text-secondary" data-id="element-325">
                No business profile set up
              </div>}
          </motion.div>
        </div>

        {/* Saved Assessments & Reports */}
        <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-326">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/legal-health-check', {
          state: {
            viewState: 'history'
          }
        })} data-id="element-327">
            <div className="flex items-center justify-between mb-4" data-id="element-328">
              <h3 className="text-lg font-bold text-primary" data-id="element-329">
                Saved Assessments
              </h3>
              <Activity className="w-5 h-5 text-secondary" data-id="element-330" />
            </div>
            <div className="p-4 bg-aliceBlue rounded-lg border border-borderColor flex items-center justify-between" data-id="element-331">
              <div className="flex items-center gap-3" data-id="element-332">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm" data-id="element-333">
                  <Building2 className="w-5 h-5 text-violetBlue" data-id="element-334" />
                </div>
                <div data-id="element-335">
                  <p className="text-sm font-bold text-primary" data-id="element-336">
                    Business Check
                  </p>
                  <p className="text-xs text-secondary" data-id="element-337">Draft • Today</p>
                </div>
              </div>
              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-full" data-id="element-338">
                40% Complete
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/reports')} data-id="element-339">
            <div className="flex items-center justify-between mb-4" data-id="element-340">
              <h3 className="text-lg font-bold text-primary" data-id="element-341">Latest Reports</h3>
              <FileText className="w-5 h-5 text-secondary" data-id="element-342" />
            </div>
            <div className="p-4 bg-aliceBlue rounded-lg border border-borderColor flex items-center justify-between" data-id="element-343">
              <div className="flex items-center gap-3" data-id="element-344">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm" data-id="element-345">
                  <Shield className="w-5 h-5 text-turquoise" data-id="element-346" />
                </div>
                <div data-id="element-347">
                  <p className="text-sm font-bold text-primary" data-id="element-348">
                    Personal Check
                  </p>
                  <p className="text-xs text-secondary" data-id="element-349">Completed • Oct 12</p>
                </div>
              </div>
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full" data-id="element-350">
                Score: 62
              </span>
            </div>
          </motion.div>
        </div>

        {/* Explainer Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm" data-id="element-351">
          <div className="flex items-start gap-4" data-id="element-352">
            <div className="w-10 h-10 bg-aliceBlue rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-353">
              <Info className="w-5 h-5 text-violetBlue" data-id="element-354" />
            </div>
            <div data-id="element-355">
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-356">
                How scores and reports work
              </h3>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-357">
                Your Legal Health Score is calculated across each area based on
                your answers. A higher score means stronger legal protections
                are in place. After completing an assessment, you'll receive a
                detailed report with section-by-section scores, risk highlights,
                and a prioritized action plan with specific next steps. You can
                download your report as a PDF or share it securely with a lawyer
                or advisor.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AppLayout>;
}